import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Sun, Activity } from 'lucide-react';
import ServiceDashboard from './components/ServiceDashboard';
import { useDarkMode } from './hooks/useDarkMode';

function App() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-900 transition-all duration-500">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-transparent to-indigo-100/20 dark:from-blue-900/10 dark:to-indigo-900/10"></div>
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <motion.header 
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="glass backdrop-blur-xl border-b border-white/20 dark:border-white/10"
        >
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <motion.div 
                className="flex items-center space-x-3"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <Activity className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                    LLM Health Monitor
                  </h1>
                  <p className="text-slate-600 dark:text-slate-300 text-sm">
                    Real-time API service status dashboard
                  </p>
                </div>
              </motion.div>
              
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleDarkMode}
                className="p-3 rounded-xl bg-white/10 dark:bg-black/10 backdrop-blur-sm border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-black/20 transition-all duration-200 shadow-lg"
              >
                {isDarkMode ? (
                  <Sun className="w-6 h-6 text-yellow-500" />
                ) : (
                  <Moon className="w-6 h-6 text-slate-600" />
                )}
              </motion.button>
            </div>
          </div>
        </motion.header>

        {/* Main Content */}
        <motion.main 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="container mx-auto px-4 py-8"
        >
          <ServiceDashboard />
        </motion.main>

        {/* Footer */}
        <motion.footer 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-center py-6 mt-8"
        >
          <div className="glass backdrop-blur-sm rounded-2xl max-w-md mx-auto px-6 py-4 border border-white/20 dark:border-white/10">
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              Last updated: <span className="font-medium text-slate-800 dark:text-slate-200">{new Date().toLocaleTimeString()}</span>
            </p>
          </div>
        </motion.footer>
      </div>
    </div>
  );
}

export default App;